/**
 * Lightweight pure-SVG radar/spider chart.
 *
 * Props:
 *   axes: [{ key, label, lowerBetter?: bool }]  — 3+ axes
 *   datasets: [{ label, color, values: { [axisKey]: number } }]  — 1..N overlays
 *   reference: { label, color, values: { [axisKey]: number } }   — optional peer/avg baseline
 *   scale: { [axisKey]: { min, max } }   — for normalising each axis
 *   size?: number (px, default 220)
 *   showLegend?: bool (default true)
 */
export default function RadarChart({
  axes,
  datasets,
  reference,
  scale,
  size = 220,
  showLegend = true,
  ringCount = 4,
  bgColor = "transparent",
  axisColor = "rgba(255,255,255,0.18)",
  labelColor = "rgba(255,255,255,0.75)",
}) {
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.38;
  const labelOffset = 16;
  const N = axes.length;

  // Normalize a value 0..1 along an axis given the scale
  const norm = (axis, v) => {
    if (v == null) return 0;
    const s = scale?.[axis.key];
    if (!s || s.max === s.min) return 0;
    let t = (v - s.min) / (s.max - s.min);
    if (axis.lowerBetter) t = 1 - t;
    return Math.max(0, Math.min(1, t));
  };

  // Cartesian point for an axis-index + radius factor (0..1)
  const point = (i, r) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / N;
    return [cx + r * radius * Math.cos(angle), cy + r * radius * Math.sin(angle)];
  };

  // Background rings
  const rings = [];
  for (let r = 1; r <= ringCount; r++) {
    const pts = Array.from({ length: N }, (_, i) => point(i, r / ringCount).join(",")).join(" ");
    rings.push(<polygon key={`ring-${r}`} points={pts} fill="none" stroke={axisColor} strokeWidth={r === ringCount ? 1 : 0.6} />);
  }

  // Axes spokes
  const spokes = axes.map((axis, i) => {
    const [x, y] = point(i, 1);
    return <line key={`spoke-${axis.key}`} x1={cx} y1={cy} x2={x} y2={y} stroke={axisColor} strokeWidth={0.6} />;
  });

  // Labels
  const labels = axes.map((axis, i) => {
    const [x, y] = point(i, 1 + labelOffset / radius);
    const anchor = Math.abs(x - cx) < 4 ? "middle" : x > cx ? "start" : "end";
    return (
      <text key={`label-${axis.key}`} x={x} y={y} textAnchor={anchor} fontSize={9} fontWeight={600} fill={labelColor} dominantBaseline="middle">
        {axis.label}
      </text>
    );
  });

  // Polygon for a dataset (or reference)
  const polygon = (ds, opacity, dashed) => {
    const pts = axes.map((axis, i) => point(i, norm(axis, ds.values[axis.key])).join(",")).join(" ");
    return (
      <g>
        <polygon points={pts} fill={ds.color} fillOpacity={opacity} stroke={ds.color} strokeWidth={1.5} strokeDasharray={dashed ? "3,3" : undefined} />
        {axes.map((axis, i) => {
          const [x, y] = point(i, norm(axis, ds.values[axis.key]));
          return <circle key={`dot-${axis.key}`} cx={x} cy={y} r={2.5} fill={ds.color} />;
        })}
      </g>
    );
  };

  return (
    <div className="inline-flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ background: bgColor }} role="img" aria-label="Performance radar chart">
        {rings}
        {spokes}
        {/* Reference (peer average) first so it's behind */}
        {reference && polygon(reference, 0.06, true)}
        {/* Each dataset */}
        {datasets.map((ds) => <g key={`ds-${ds.label}`}>{polygon(ds, 0.22, false)}</g>)}
        {labels}
      </svg>
      {showLegend && (datasets.length > 0 || reference) && (
        <div className="flex items-center gap-3 mt-1 flex-wrap justify-center">
          {datasets.map((ds) => (
            <div key={`legend-${ds.label}`} className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: ds.color }} />
              <span className="text-[10px] font-medium" style={{ color: labelColor }}>{ds.label}</span>
            </div>
          ))}
          {reference && (
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: reference.color, opacity: 0.5 }} />
              <span className="text-[10px] font-medium italic" style={{ color: labelColor }}>{reference.label}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
